import { useEffect, useMemo, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import { useAuth } from '../../contexts/AuthContext';
import { getCommissionRecords } from '../../lib/api';

const formatCurrency = (amount) =>
  new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(
    Number(amount || 0),
  );

const formatDate = (value) => {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
};

const chipClass = (status) => (
  status === 'paid'
    ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
    : status === 'approved'
      ? 'bg-blue-50 text-blue-700 border-blue-200'
      : 'bg-amber-50 text-amber-700 border-amber-200'
);

const normalizeRecord = (item) => ({
  id: item.id,
  amount: Number(item.amount || 0),
  status: item.status,
  bank: item.bank_name || '—',
  loanId: item.loan_application_id,
  createdAt: item.created_at,
  paidAt: item.paid_at,
});

export default function AgentPayouts() {
  const location = useLocation();
  const { token } = useAuth();
  const [rows, setRows] = useState([]);
  const [paidRows, setPaidRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const mode = (() => {
    if (location.pathname.includes('/agent/payouts/history')) return 'history';
    return 'status';
  })();

  useEffect(() => {
    let active = true;
    async function load() {
      if (!token) return;
      setLoading(true);
      setError('');
      try {
        const [listResp, paidResp] = await Promise.all([
          getCommissionRecords(
            token,
            mode === 'history' ? { status: 'paid' } : { statuses: ['pending', 'approved'] },
          ),
          getCommissionRecords(token, { status: 'paid' }),
        ]);
        const items = Array.isArray(listResp?.data?.items) ? listResp.data.items : [];
        const paidItems = Array.isArray(paidResp?.data?.items) ? paidResp.data.items : [];
        if (active) {
          setRows(items.map(normalizeRecord));
          setPaidRows(paidItems.map(normalizeRecord));
        }
      } catch (err) {
        if (!active) return;
        setRows([]);
        setPaidRows([]);
        setError(err?.message || 'Failed to load payout data');
      } finally {
        if (active) setLoading(false);
      }
    }
    load();
    return () => {
      active = false;
    };
  }, [mode, token]);

  const paidAmount = useMemo(() => paidRows.reduce((sum, row) => sum + row.amount, 0), [paidRows]);

  return (
    <div className="space-y-6" data-testid="agent-payouts">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold font-['Plus_Jakarta_Sans']">
            {mode === 'history' ? 'Payout History' : 'Payout Status'}
          </h1>
          <p className="text-muted-foreground mt-1">
            Payout visibility follows real commission status until a dedicated payout workflow is added.
          </p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button asChild size="sm" variant={mode === 'status' ? 'default' : 'outline'}>
            <Link to="/agent/payouts/status">Status</Link>
          </Button>
          <Button asChild size="sm" variant={mode === 'history' ? 'default' : 'outline'}>
            <Link to="/agent/payouts/history">History</Link>
          </Button>
        </div>
      </div>

      <Card className="border border-border">
        <CardHeader>
          <CardTitle>Paid Out</CardTitle>
          <CardDescription>Commission entries already marked as paid.</CardDescription>
        </CardHeader>
        <CardContent>
          <p className="text-3xl font-bold text-emerald-600">{formatCurrency(paidAmount)}</p>
        </CardContent>
      </Card>

      <Card className="border border-border">
        <CardHeader>
          <CardTitle>{mode === 'history' ? 'All Payout Entries' : 'Open Payout Entries'}</CardTitle>
          <CardDescription>
            {mode === 'history' ? 'All commission entries with payout-related statuses.' : 'Pending and approved commission entries.'}
          </CardDescription>
        </CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Loan</TableHead>
                <TableHead>Amount</TableHead>
                <TableHead>Created</TableHead>
                <TableHead>Paid</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Bank</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-10 text-muted-foreground">
                    Loading payout data...
                  </TableCell>
                </TableRow>
              ) : null}
              {!loading && error ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-10 text-muted-foreground">
                    {error}
                  </TableCell>
                </TableRow>
              ) : null}
              {!loading && !error && rows.map((row) => (
                <TableRow key={row.id}>
                  <TableCell className="font-mono text-sm">{row.loanId}</TableCell>
                  <TableCell className="font-medium">{formatCurrency(row.amount)}</TableCell>
                  <TableCell>{formatDate(row.createdAt)}</TableCell>
                  <TableCell>{formatDate(row.paidAt)}</TableCell>
                  <TableCell>
                    <Badge variant="outline" className={chipClass(row.status)}>
                      {row.status}
                    </Badge>
                  </TableCell>
                  <TableCell>{row.bank}</TableCell>
                </TableRow>
              ))}
              {!loading && !error && rows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-10 text-muted-foreground">
                    No payout entries found.
                  </TableCell>
                </TableRow>
              ) : null}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}
